import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Modal, Form, Alert, Table, Badge } from 'react-bootstrap';
import { connect } from 'react-redux';

const UserManagement = ({ auth }) => {
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'user',
    directories: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, routesResponse] = await Promise.all([
        fetch('/api/admin/users', { credentials: 'include' }),
        fetch('/api/admin/routes', { credentials: 'include' })
      ]);

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users);
      }

      if (routesResponse.ok) {
        const routesData = await routesResponse.json();
        setRoutes(routesData.routes);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowAddModal(false);
        setFormData({ email: '', first_name: '', last_name: '', role: 'user', directories: [] });
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingUser(null);
        setFormData({ email: '', first_name: '', last_name: '', role: 'user', directories: [] });
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await fetch(`/api/admin/users/${userId}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok) {
          fetchData();
        } else {
          const errorData = await response.json();
          setError(errorData.error);
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'user',
      directories: []
    });
    setShowAddModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    const allDirectories = routes.filter(route => route.requires_auth && route.route_path !== '/logout' && route.route_path !== '/admin-console' && route.route_path !== '/settings').map(route => route.route_path);
    
    // Get directories from permissions array (ek_permissions is an array)
    let userDirectories = [];
    if (user.permissions && user.permissions.length > 0) {
      const directories = user.permissions[0].directories;
      // Handle both string and array formats
      if (typeof directories === 'string') {
        try {
          userDirectories = JSON.parse(directories);
        } catch (e) {
          console.error('Error parsing directories:', e);
          userDirectories = [];
        }
      } else if (Array.isArray(directories)) {
        userDirectories = directories;
      }
    }
    
    const finalDirectories = user.role === 'admin' ? allDirectories : userDirectories;
    
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      directories: finalDirectories
    });
    setShowEditModal(true);
  };

  const toggleDirectory = (directory) => {
    // Don't allow changes for admin users
    if (formData.role === 'admin') {
      return;
    }
    
    const newDirectories = formData.directories.includes(directory)
      ? formData.directories.filter(d => d !== directory)
      : [...formData.directories, directory];
    
    setFormData({ ...formData, directories: newDirectories });
  };


  if (loading) {
    return (
      <div>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading user management...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>User Management</h4>
              <Button variant="success" onClick={openAddModal}>
                Add New User
              </Button>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Directories</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.first_name} {user.last_name}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg={user.role === 'admin' ? 'danger' : 'primary'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        {user.permissions?.[0]?.directories?.map(dir => (
                          <Badge key={dir} bg="secondary" className="me-1">
                            {dir}
                          </Badge>
                        ))}
                      </td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => openEditModal(user)}
                          className="me-2"
                        >
                          Edit
                        </Button>
                        {user.id !== auth.id && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add User Modal - Reference implementation for modal pattern, see /.claude/modal-pattern.md */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Directory Access</Form.Label>
              {routes.filter(route => route.requires_auth && route.route_path !== '/logout' && route.route_path !== '/admin-console' && route.route_path !== '/settings').map(route => (
                <Form.Check
                  key={route.id}
                  type="checkbox"
                  label={route.route_name}
                  checked={formData.directories.includes(route.route_path)}
                  onChange={() => toggleDirectory(route.route_path)}
                />
              ))}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add User
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit User Modal - Reference implementation for modal pattern, see /.claude/modal-pattern.md */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} key={editingUser?.id || 'edit-modal'}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => {
                  if (editingUser?.role !== 'admin') {
                    setFormData({ ...formData, email: e.target.value });
                  }
                }}
                required
                disabled={editingUser?.role === 'admin'}
              />
              {editingUser?.role === 'admin' && (
                <Form.Text className="text-muted">
                  Admin user email cannot be changed
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.first_name}
                onChange={(e) => {
                  if (editingUser?.role !== 'admin') {
                    setFormData({ ...formData, first_name: e.target.value });
                  }
                }}
                required
                disabled={editingUser?.role === 'admin'}
              />
              {editingUser?.role === 'admin' && (
                <Form.Text className="text-muted">
                  Admin user name cannot be changed
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.last_name}
                onChange={(e) => {
                  if (editingUser?.role !== 'admin') {
                    setFormData({ ...formData, last_name: e.target.value });
                  }
                }}
                required
                disabled={editingUser?.role === 'admin'}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                disabled={editingUser?.role === 'admin'}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
              {editingUser?.role === 'admin' && (
                <Form.Text className="text-muted">
                  Admin users cannot be downgraded
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Directory Access</Form.Label>
              {routes.filter(route => route.requires_auth && route.route_path !== '/logout' && route.route_path !== '/admin-console' && route.route_path !== '/settings').map(route => (
                <Form.Check
                  key={route.id}
                  type="checkbox"
                  label={route.route_name}
                  checked={formData.directories.includes(route.route_path) || formData.role === 'admin'}
                  onChange={() => toggleDirectory(route.route_path)}
                  disabled={formData.role === 'admin'}
                />
              ))}
              {formData.role === 'admin' && (
                <Form.Text className="text-muted">
                  Admin users have access to all directories
                </Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowEditModal(false);
                setEditingUser(null);
                setFormData({ email: '', first_name: '', last_name: '', role: 'user', directories: [] });
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update User
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(UserManagement);

